/**
 * The earthworks rate-card form (POST / PATCH /earthworks/rates).
 *
 * Currency is NEVER sent — a rate is in the company's currency, stamped by the
 * server (`currency_not_settable`). The fields that apply to one kind only are
 * shown — and sent — for that kind only: a crew role on a labour rate, a unit
 * OR an equipment type on an equipment or trucking rate, a markup on a
 * passthrough rate, "includes operator" on an equipment rate (0600 — the
 * builder then does not bill that unit's operator as labour; the API refuses it
 * on any other kind). Volume / area / mass units are the tenant's (meta.units).
 *
 * A rate whose code already covers some of the same days for the same card is
 * refused 409 `rate_overlap`; the form shows the sentence from `ewRefusal`.
 */
import React, { useState } from 'react';
import { blankToNull, ewPriceUnits, ewRateTakesEquipment, toInput } from '../../lib/earthworks';
import type { EwCatalog, EwMeta, EwRate, EwRateWrite } from '../../types/earthworks';
import { EW_DATE_INPUT, EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError } from './EwBits';

export interface EwRateFormOptions {
  customers: { id: string; name: string }[];
  equipment: { id: string; name: string; unitNumber?: string | null }[];
  equipmentTypes: { id: string; name: string }[];
}

export function EwRateForm({
  rate,
  catalog,
  meta,
  options,
  defaultCustomerId,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  rate?: EwRate | null;
  catalog: EwCatalog | null;
  meta: EwMeta | null;
  options: EwRateFormOptions;
  defaultCustomerId?: string;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: EwRateWrite) => void;
}) {
  const [code, setCode] = useState(rate?.code ?? '');
  const [name, setName] = useState(rate?.name ?? '');
  const [kind, setKind] = useState(rate?.kind ?? '');
  const [unit, setUnit] = useState(rate?.unit ?? '');
  const [amount, setAmount] = useState(toInput(rate?.rate));
  const [passthrough, setPassthrough] = useState(rate?.passthrough === true);
  const [markup, setMarkup] = useState(toInput(rate?.markupPct));
  const [material, setMaterial] = useState(rate?.material ?? '');
  const [customerId, setCustomerId] = useState(rate ? rate.customerId ?? '' : defaultCustomerId ?? '');
  const [crewRole, setCrewRole] = useState(rate?.crewRole ?? '');
  const [equipmentId, setEquipmentId] = useState(rate?.equipmentId ?? '');
  const [equipmentTypeId, setEquipmentTypeId] = useState(rate?.equipmentTypeId ?? '');
  const [from, setFrom] = useState(rate?.effectiveFrom ?? meta?.today ?? '');
  const [to, setTo] = useState(rate?.effectiveTo ?? '');
  const [description, setDescription] = useState(rate?.description ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');
  const [includesOperator, setIncludesOperator] = useState(rate?.includesOperator === true);
  const [problem, setProblem] = useState<string | null>(null);
  const currency = rate?.currency ?? meta?.currency ?? null;
  const units = ewPriceUnits(catalog?.priceUnits, meta, rate?.unit);
  const takesEquipment = ewRateTakesEquipment(kind);

  const submit = () => {
    setProblem(null);
    if (!code.trim()) return setProblem('Enter a short code for the rate.');
    if (!name.trim()) return setProblem('Enter the rate’s name.');
    if (!kind || !unit) return setProblem('Pick what kind of charge it is and its unit.');
    if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) return setProblem('Enter the rate as a number of 0 or more.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setProblem('Enter the day the rate starts.');
    if (to && to < from) return setProblem('The end day can’t be before the start day.');
    if (takesEquipment && equipmentId && equipmentTypeId) return setProblem('A rate prices one unit or one equipment type, not both. Pick one.');
    const body: EwRateWrite = {
      code: code.trim(),
      name: name.trim(),
      kind,
      unit,
      rate: amount.trim(),
      passthrough,
      effective_from: from,
      effective_to: to || null,
      customer_id: customerId || null,
      material: blankToNull(material),
      description: blankToNull(description),
      notes: blankToNull(notes),
    };
    if (passthrough) body.markup_pct = blankToNull(markup);
    else if (rate?.markupPct != null) body.markup_pct = null;
    if (kind === 'labour') body.crew_role = crewRole || null;
    else if (rate?.crewRole) body.crew_role = null;
    if (kind === 'equipment') body.includes_operator = includesOperator;
    else if (rate?.includesOperator) body.includes_operator = false;
    if (takesEquipment) {
      body.equipment_id = equipmentId || null;
      body.equipment_type_id = equipmentTypeId || null;
    } else {
      if (rate?.equipmentId) body.equipment_id = null;
      if (rate?.equipmentTypeId) body.equipment_type_id = null;
    }
    onSubmit(body);
  };

  const pick = (label: string, value: string, set: (v: string) => void, list: { value: string; label: string }[], blank: string) => (
    <EwField label={label}>
      <select className={EW_INPUT} aria-label={label.replace(' *', '')} value={value} onChange={(e) => set(e.target.value)}>
        <option value="">{blank}</option>
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </EwField>
  );

  return (
    <div data-testid="ew-rate-form" className="space-y-4 px-4 py-4">
      <EwFormError message={problem ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <EwField label="Code *" hint="Short, e.g. EXC-320. Unique per card and period.">
          <input className={EW_INPUT} aria-label="Code" maxLength={30} value={code} onChange={(e) => setCode(e.target.value)} />
        </EwField>
        <EwField label="Name *">
          <input className={EW_INPUT} aria-label="Name" maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
        </EwField>
        {pick(
          'Card',
          customerId,
          setCustomerId,
          options.customers.map((c) => ({ value: c.id, label: c.name })),
          'Default card (every customer)',
        )}
        {pick('Kind *', kind, setKind, catalog?.priceKinds ?? [], 'Pick one')}
        {pick('Unit *', unit, setUnit, units, 'Pick one')}
        <EwField label={`Rate (${currency ?? 'company currency'}) *`} hint="The company’s currency, set in Settings.">
          <input className={EW_INPUT} inputMode="decimal" aria-label="Rate" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </EwField>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={passthrough} onChange={(e) => setPassthrough(e.target.checked)} aria-label="Passthrough" />
          Passthrough (billed at cost)
        </label>
        {passthrough && (
          <EwField label="Markup %" hint="Optional. Added on top of the cost.">
            <input className={EW_INPUT} inputMode="decimal" aria-label="Markup %" value={markup} onChange={(e) => setMarkup(e.target.value)} />
          </EwField>
        )}
        {kind === 'material' && (
          <EwField label="Material" hint="e.g. pit run, 3/4 crush, topsoil">
            <input className={EW_INPUT} aria-label="Material" maxLength={100} value={material} onChange={(e) => setMaterial(e.target.value)} />
          </EwField>
        )}
        {kind === 'equipment' && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
            <input type="checkbox" checked={includesOperator} onChange={(e) => setIncludesOperator(e.target.checked)} aria-label="Includes operator" />
            Includes operator (their hours on this unit are not billed again as labour)
          </label>
        )}
        {kind === 'labour' && pick('Crew role', crewRole, setCrewRole, catalog?.crewRoles ?? [], 'Any role')}
        {takesEquipment && (
          <>
            {pick(
              'Equipment unit',
              equipmentId,
              setEquipmentId,
              options.equipment.map((q) => ({ value: q.id, label: q.unitNumber ? `${q.unitNumber} · ${q.name}` : q.name })),
              'Any unit',
            )}
            {pick('Equipment type', equipmentTypeId, setEquipmentTypeId, options.equipmentTypes.map((q) => ({ value: q.id, label: q.name })), 'Any type')}
          </>
        )}
        <EwField label="Starts *">
          <input className={EW_DATE_INPUT} type="date" aria-label="Starts" value={from} onChange={(e) => setFrom(e.target.value)} />
        </EwField>
        <EwField label="Ends" hint="Blank = no end day.">
          <input className={EW_DATE_INPUT} type="date" aria-label="Ends" value={to} onChange={(e) => setTo(e.target.value)} />
        </EwField>
      </div>
      <EwField label="Description" hint="Optional.">
        <input className={EW_INPUT} aria-label="Description" maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />
      </EwField>
      <EwField label="Notes">
        <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Notes" maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </EwField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={EW_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}
